import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import SolutionCard from "@/components/SolutionCard";
import { ArticleFav, ArticleShare } from "@/components/ArticleActions";
import { trackEntite } from "@/lib/track";
import Seo from "@/components/Seo";
import "./Accueil.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Logo partenaire : fichier televerse (/api/files/...) ou URL externe / data-URI.
const logoSrc = (l) => !l ? l : (/^(https?:|data:)/.test(l) ? l : `${BACKEND}${l}`);
// Retire les balises puis décode toutes les entités HTML (numériques et nommées)
// renvoyées par WordPress, via un textarea hors DOM (aucune balise interprétée).
const stripHtml = (h) => {
  if (!h) return "";
  const ta = document.createElement("textarea");
  ta.innerHTML = h.replace(/<[^>]+>/g, "");
  return (ta.value || "").replace(/\s+/g, " ").trim();
};
const fmtDateFr = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";
const BK = process.env.REACT_APP_BACKEND_URL || "";
const mediaUrl = (u) => (u && u.startsWith("/") ? BK + u : (u || ""));

// Idées d'exemple affichées TANT QU'il n'y a pas de vraies idées proposées.
// Dès qu'il existe de vraies idées (collection idees, hors « classée »), ce sont
// leurs titres qui tournent à la place - sur l'accueil et sur l'index solutions.
const IDEES_EXEMPLE = [
  "💡 « Un carnet de santé numérique partagé »",
  "💡 « Une plateforme de transport inter-villes »",
  "💡 « Un outil de financement pour artisans »",
];
const bulleIdee = (titre) => `💡 « ${titre} »`;

const ARTICLES_FALLBACK = [
  { favId: "accueil-ara-market", lien: "/articles", slug: "decryptages", catName: "Décryptages", titre: "Ara Market : le pari du reconditionné pour rendre la tech plus accessible", extrait: "Comment ce marketplace rend smartphones et ordinateurs reconditionnés accessibles en Afrique de l'Ouest.", date: "29 juillet 2026", img: "" },
  { favId: "accueil-seckin", lien: "/articles", slug: "rencontres", catName: "Rencontres", titre: "Salon des Entrepreneurs Congolais SECKIN 2026, 3e édition à Kinshasa", extrait: "Entrepreneurs, investisseurs et startups congolaises réunis les 5, 6 et 8 août.", date: "29 juillet 2026", img: "" },
  { favId: "accueil-afrique-solutions", lien: "/articles", slug: "rencontres", catName: "Rencontres", titre: "Semaine l'Afrique des Solutions 2026, la 4e édition arrive à Paris", extrait: "Entrepreneurs, journalistes et diaspora se retrouvent à Paris les 23 et 24 octobre.", date: "17 juillet 2026", img: "" },
  { favId: "accueil-diaspora-mauritanienne", lien: "/articles", slug: "diaspora", catName: "Diaspora", titre: "Village de la Diaspora Mauritanienne, un forum économique à Marseille", extrait: "Entrepreneurs, investisseurs et institutions autour de la Mauritanie, le 14 novembre.", date: "17 juillet 2026", img: "" },
  { favId: "accueil-afroverso", lien: "/articles", slug: "culture", catName: "Culture", titre: "Afroverso à Nantes, le rendez-vous des cultures africaines et afro-descendantes", extrait: "Le festival revient du 2 au 5 juillet pour célébrer les cultures du continent.", date: "15 juillet 2026", img: "" },
];

const ARTICLES_CACHE_KEY = "accueil_articles_cache_v1";
const lireArticlesCache = () => {
  try {
    const c = JSON.parse(localStorage.getItem(ARTICLES_CACHE_KEY));
    return Array.isArray(c) && c.length ? c : null;
  } catch { return null; }
};

export default function Accueil() {
  const nav = useNavigate();
  const [partenaire, setPartenaire] = useState(null);
  const [bande, setBande] = useState({ actif: false, items: [] });
  // Affiche instantanément les derniers articles connus (cache) au refresh,
  // sinon le fallback statique au tout premier accès.
  const [articles, setArticles] = useState(() => lireArticlesCache() || ARTICLES_FALLBACK);
  // Compteurs de la section « Chiffres » : adaptés automatiquement aux vrais contenus.
  // Valeurs de repli le temps du fetch (et si le réseau échoue).
  const [stats, setStats] = useState({ solutions: 247, pays: 14, articles: 380, idees: 63 });
  // Bulles d'idées : vraies idées si disponibles, sinon exemples (fallback).
  const [ideesBulles, setIdeesBulles] = useState(IDEES_EXEMPLE.map((t) => ({ id: null, texte: t })));
  const ideeRotationRef = useRef(null);
  // Solutions mises en avant sur l'accueil (vraies données, sponsorisées d'abord).
  const [solutionsTop, setSolutionsTop] = useState([]);
  useEffect(() => {
    http.get("/solutions").then((r) => {
      const list = r.data || [];
      const ordre = [...list.filter((s) => s.sponsorise), ...list.filter((s) => !s.sponsorise)];
      setSolutionsTop(ordre.slice(0, 3));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Articles natifs (migrés de WordPress) + articles sponsorisés (par l'admin).
        const [items, sponsorIds] = await Promise.all([
          http.get("/articles", { params: { par_page: 10 } }).then(r => r.data?.items || []).catch(() => []),
          http.get("/articles/sponsors").then(r => r.data).catch(() => []),
        ]);
        const sponsorSet = new Set((sponsorIds || []).map(String));
        const mapArt = (a) => ({
          favId: String(a.id),
          lien: `/article/${a.slug}`,
          slug: (a.categorie || {}).slug || "",
          catName: (a.categorie || {}).nom || "",
          titre: stripHtml(a.titre || ""),
          extrait: stripHtml(a.extrait || "").slice(0, 160),
          date: fmtDateFr(a.date),
          img: mediaUrl(a.image),
          sponsorise: sponsorSet.has(String(a.wp_id)) || sponsorSet.has(String(a.id)),
        });
        const mapped = (items || []).map(mapArt);
        // Sponsorisés en tête (ordre stable), puis les derniers ; dédupliqué, plafond 10.
        const vus = new Set();
        const ordonne = [...mapped.filter(a => a.sponsorise), ...mapped.filter(a => !a.sponsorise)]
          .filter(a => !vus.has(a.favId) && vus.add(a.favId))
          .slice(0, 10);
        if (alive && ordonne.length) {
          setArticles(ordonne);
          try { localStorage.setItem(ARTICLES_CACHE_KEY, JSON.stringify(ordonne)); } catch { /* quota/private mode */ }
        }
      } catch { /* garde le fallback statique */ }
    })();
    return () => { alive = false; };
  }, []);


  // Schéma magazine élastique (un seul bloc, 5 à 10 articles) : 1re carte vedette
  // (colonne gauche, toujours 2 rangées de haut), dernière carte en bannière pleine
  // largeur, et les cartes du milieu réparties sur exactement 2 rangées. Le nombre de
  // colonnes s'adapte pour que le milieu tienne sur 2 rangées (5-6 → 3 col, 7-8 → 4 col…).
  // Si le nombre de cartes du milieu est impair, la dernière passe en « large » (2 colonnes)
  // pour combler la rangée et éviter une carte orpheline.
  const mag = useMemo(() => {
    const n = articles.length;
    const milieu = n - 2;                                  // cartes entre vedette et bannière
    const colsMilieu = milieu > 0 ? Math.ceil(milieu / 2) : 1; // colonnes pour tenir sur 2 rangées
    return {
      actif: n >= 5,
      cols: 1 + colsMilieu,                                // total colonnes (vedette + milieu)
      wideIndex: milieu > 0 && milieu % 2 === 1 ? n - 2 : -1, // dernière carte du milieu si impair
      lastIndex: n - 1,
      dense: n > 6,                                        // >6 articles : 2 cartes/ligne sur mobile
    };
  }, [articles]);

  // Recharge les compteurs depuis les vraies sources : /public/stats (solutions testées,
  // pays, idées) + total d'articles WordPress (en-tête X-WP-Total). Appelé au montage et
  // à chaque retour sur l'onglet, pour refléter les publications admin sans rechargement.
  const rafraichirCompteurs = useCallback(() => {
    http.get("/public/stats").then(r => setStats(s => {
      const n = { ...s, ...r.data };
      return (n.solutions === s.solutions && n.pays === s.pays && n.idees === s.idees) ? s : n;
    })).catch(() => {});
    http.get("/articles", { params: { par_page: 1 } })
      .then(r => { const t = r.data?.total; if (Number.isFinite(t)) setStats(s => t === s.articles ? s : { ...s, articles: t }); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    http.get("/settings/partenaire").then(r => setPartenaire(r.data)).catch(() => {});
    http.get("/partenaires-bande/public").then(r => setBande(r.data)).catch(() => {});
    rafraichirCompteurs();
  }, [rafraichirCompteurs]);

  // Rafraîchit les compteurs quand l'utilisateur revient sur l'onglet (ex. après qu'un
  // admin vient de publier une solution ou un article), et périodiquement (60 s) tant
  // que l'onglet reste visible - pour refléter une publication même onglet ouvert.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") rafraichirCompteurs(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const timer = setInterval(() => { if (document.visibilityState === "visible") rafraichirCompteurs(); }, 60000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(timer);
    };
  }, [rafraichirCompteurs]);

  // Compteurs animés : (ré)armés quand les vrais chiffres arrivent, pour ne jamais
  // figer une valeur de repli si la section était déjà visible au chargement.
  useEffect(() => {
    const compteurs = document.querySelectorAll('.chiffre b');
    const chiffresEl = document.querySelector('.chiffres');
    if (!compteurs.length || !chiffresEl) return;
    let lance = false;
    const animer = () => {
      if (lance) return;
      lance = true;
      compteurs.forEach((el) => {
        const cible = parseInt(el.getAttribute('data-cible'), 10) || 0;
        const suffixe = el.getAttribute('data-suffixe') || '';
        const duree = 1400, depart = performance.now();
        const etape = (t) => {
          const p = Math.min((t - depart) / duree, 1);
          el.textContent = Math.floor(p * cible) + suffixe;
          if (p < 1) requestAnimationFrame(etape); else el.textContent = cible + suffixe;
        };
        requestAnimationFrame(etape);
      });
    };
    const obs = new IntersectionObserver(
      (entrees) => entrees.forEach((e) => { if (e.isIntersecting) animer(); }),
      { threshold: 0.4 }
    );
    obs.observe(chiffresEl);
    return () => obs.disconnect();
  }, [stats]);

  useEffect(() => {

    /* ---- Reveal animations ---- */
    const elementsRevelables = document.querySelectorAll('.accueil-root .reveal, .accueil-root .reveal-enfant');
    const observateurRevelation = new IntersectionObserver(
      (entrees) => {
        entrees.forEach((entree) => {
          if (entree.isIntersecting) {
            entree.target.classList.add('visible');
            observateurRevelation.unobserve(entree.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    elementsRevelables.forEach((el) => observateurRevelation.observe(el));

    return () => {
      observateurRevelation.disconnect();
    };
  }, []);

  // Récupère les vraies idées (hors « classée sans suite ») ; s'il y en a, ce sont
  // leurs titres qui alimentent la rotation, sinon on garde les exemples.
  useEffect(() => {
    http.get("/idees").then((r) => {
      const vraies = (r.data || [])
        .filter((i) => i.statut !== "classee" && (i.titre || "").trim())
        .map((i) => ({ id: i.id, texte: bulleIdee(i.titre.trim()) }));
      if (vraies.length) setIdeesBulles(vraies);
    }).catch(() => {});
  }, []);

  // Rotation des bulles d'idées (repart proprement quand la liste change).
  useEffect(() => {
    const zoneIdee = ideeRotationRef.current;
    if (!zoneIdee) return;
    zoneIdee.innerHTML = "";
    let indexIdee = 0;
    const afficherIdeeSuivante = () => {
      const ancien = zoneIdee.querySelector('.idee-bulle');
      const nouveau = document.createElement('div');
      nouveau.className = 'idee-bulle';
      const item = ideesBulles[indexIdee % ideesBulles.length];
      nouveau.textContent = typeof item === "string" ? item : item.texte;
      nouveau.style.cursor = "pointer";
      nouveau.onclick = () => nav(item && item.id ? `/idees?open=${item.id}` : "/idees");
      zoneIdee.appendChild(nouveau);
      requestAnimationFrame(() => nouveau.classList.add('actif'));
      if (ancien) {
        ancien.classList.remove('actif');
        setTimeout(() => ancien.remove(), 500);
      }
      indexIdee++;
    };
    afficherIdeeSuivante();
    // Une seule idée : pas de rotation, on l'affiche fixe.
    if (ideesBulles.length < 2) return () => { zoneIdee.innerHTML = ""; };
    const ideeInterval = setInterval(afficherIdeeSuivante, 2800);
    return () => { clearInterval(ideeInterval); zoneIdee.innerHTML = ""; };
  }, [ideesBulles]);

  // L'observer reveal ci-dessus s'installe au montage ; l'encart partenaire est rendu
  // après un fetch async, donc on ré-observe les éléments reveal tardifs à son arrivée.
  useEffect(() => {
    if (!partenaire?.actif) return;
    const els = document.querySelectorAll('.accueil-root .reveal:not(.visible), .accueil-root .reveal-enfant:not(.visible)');
    if (!els.length) return;
    const obs = new IntersectionObserver((entrees) => {
      entrees.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); } });
    }, { threshold: 0.2 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [partenaire]);

  return (
    <div className="accueil-root">
      <Seo
        description="AfriFuté Atlas, le moteur de connaissance des solutions numériques africaines : découvrez, comparez et valorisez les innovations qui transforment le continent."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "AfriFuté Atlas",
          url: process.env.REACT_APP_SITE_URL || undefined,
          description: "Le moteur de connaissance des solutions numériques africaines.",
        }}
      />

      {/* ===== INTRO ===== */}
      <div className="intro">
        <div className="intro-inner">
          <div className="devise">
            <span className="devise-mot" style={{animationDelay:'.15s'}}>Utile</span>
            <span className="devise-point" style={{animationDelay:'.5s'}}></span>
            <span className="devise-mot or" style={{animationDelay:'.6s'}}>Concret</span>
            <span className="devise-point" style={{animationDelay:'.95s'}}></span>
            <span className="devise-mot" style={{animationDelay:'1.05s'}}>Actionnable</span>
          </div>
          <div className="devise-soulignement"></div>
          <h1>
            <span className="mot-titre" style={{animationDelay:'1.7s'}}>Vous ne cherchez pas</span>{' '}
            <span className="mot-titre" style={{animationDelay:'1.9s'}}>juste une solution.</span>{' '}
            <span className="g mot-titre" style={{animationDelay:'2.1s'}}>Vous voulez savoir si elle est faite pour vous.</span>
          </h1>
          <p className="intro-texte">AfriFuté explique le pourquoi, le comment et pour qui jusqu'à ce qu'une solution africaine devienne un geste simple à adopter dans votre quotidien ou votre activité.</p>
        </div>
      </div>

      {/* ===== ARTICLES ===== */}
      <div className="section" style={{paddingTop:'22px'}}>
        <div className="section-head reveal">
          <span className="tag">Éditorial</span>
          <h2>Vous repartez en sachant quoi faire.</h2>
          <p>Chaque article répond aux mêmes questions : à quoi sert cette solution, à qui elle s'adresse et comment vous en servir dès aujourd'hui.</p>
        </div>
        <div className="ed-root grid-articles-ed" style={{background:'transparent'}}>
          <div className={`ed-g3 ${mag.actif ? "ed-g3-mag" : ""} ${mag.dense ? "ed-g3-mob2" : ""}`} style={mag.actif ? { "--mag-cols": mag.cols } : undefined}>
            {articles.map((a, i) => {
              const externe = a.lien.startsWith('http');
              const role = i === 0 ? "ed-c-featured" : i === mag.lastIndex ? "ed-c-banner" : i === mag.wideIndex ? "ed-c-wide" : "";
              return (
                <a key={a.favId} className={`ed-card ${role}`} href={a.lien} target={externe ? "_blank" : undefined} rel={externe ? "noreferrer" : undefined} onClick={() => trackEntite("article", "clic", a.slug || a.favId)}>
                  {a.img
                    ? <img className="ed-c-img" src={a.img} alt="" decoding="async"
                        loading={i === 0 ? "eager" : "lazy"}
                        fetchpriority={i === 0 ? "high" : undefined} />
                    : <div className="ed-c-img" style={{background:'var(--navy)'}}></div>}
                  <div className="ed-c-body">
                    <div className="art-cat-row">
                      <span className="ed-c-cat-wrap">
                        <span className={`ed-c-cat cat-${a.slug}`}>{a.catName}</span>
                        {a.sponsorise && <span className="ed-c-sponsor">Sponsorisé</span>}
                      </span>
                      <span className="art-actions">
                        <ArticleShare id={a.slug || a.favId} lien={a.lien} titre={a.titre} />
                        <ArticleFav id={a.favId} onNeedAuth={() => nav("/connexion")} />
                      </span>
                    </div>
                    <div className="ed-c-title">{a.titre}</div>
                    <p className="ed-c-xp">{a.extrait}</p>
                    <div className="ed-c-date">{a.date}</div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
        <a className="btn-decouvrir" href="/articles">Lire tous les articles</a>
      </div>

      <div className="fil-couleur"><span className="c1"></span><span className="c2"></span><span className="c3"></span></div>

      {/* ===== CHIFFRES ===== */}
      <div className="chiffres">
        <div className="chiffres-tag">Chaque solution ajoutée est une option de plus pour résoudre un problème concret, chaque article une explication de plus pour savoir l'utiliser.</div>
        <div className="chiffres-row">
          <div className="chiffre"><b data-cible={stats.solutions}>0</b><span>Solutions testées</span></div>
          <div className="chiffre"><b data-cible={stats.articles}>0</b><span>Articles publiés</span></div>
          <div className="chiffre"><b data-cible={stats.idees}>0</b><span>Idées proposées</span></div>
        </div>
      </div>

      {/* ===== PARTENAIRE 1 ===== */}
      {partenaire?.actif && partenaire?.titre && (
        <div className="section" style={{paddingTop:'50px',paddingBottom:'50px'}}>
          <div className="encart-partenaire">
            <div className="partenaire-box reveal">
              <div className="partenaire-row">
                <div className="logo-partenaire">
                  {partenaire.logo_url ? <img src={partenaire.logo_url} alt="logo" loading="lazy" decoding="async" style={{width:'100%',height:'100%',objectFit:'contain'}} /> : <span>LOGO<br />PARTENAIRE</span>}
                </div>
                <div className="texte">
                  <span className="tag-partenaire">Entreprise mise en lumière</span>
                  <h3>{partenaire.titre}</h3>
                  <p>{partenaire.description}</p>
                  <a className="btn-mini" href={partenaire.lien_bouton || '#'}>{partenaire.texte_bouton || 'Voir l\'offre'}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fil-couleur"><span className="c1"></span><span className="c2"></span><span className="c3"></span></div>

      {/* ===== SOLUTIONS ATLAS ===== */}
      <div className="section section-atlas">
        <div className="section-head reveal">
          <span className="tag">Atlas</span>
          <h2>Trouvez la solution qui répond à votre besoin.</h2>
          <p>Un nom, un usage, un lien vérifié. Vous gagnez le temps qu'aurait pris une recherche dispersée sur dix sites différents.</p>
        </div>
        <div className="grid-solutions">
          {solutionsTop.map((s, i) => <SolutionCard key={s.id} s={s} delay={i * 0.12} />)}
        </div>
        <a className="btn-decouvrir or" href="/solutions">Voir les solutions</a>
      </div>

      {/* ===== IDEE CALLOUT ===== */}
      <div className="section">
        <div className="idee-callout reveal">
          <div className="motif-africain"></div>
          <span className="idee-eyebrow">La boîte à idées de l'Afrique</span>
          <h2>Et si la prochaine solution africaine commençait par votre idée&nbsp;?</h2>
          <p>Pas une entreprise qui existe déjà : un besoin non résolu, aussi simple soit-il. Décrivez-le en quelques mots. Peut-être qu'un entrepreneur la construira demain.</p>
          <div className="idee-rotation" ref={ideeRotationRef}></div>
          <div className="idee-actions">
            <button className="btn-decouvrir or" onClick={() => nav("/proposer-idee")}>Proposer une idée</button>
            <button className="btn-decouvrir" onClick={() => nav("/idees")}>Voir les idées</button>
          </div>
        </div>
      </div>

      {/* ===== NOS SERVICES ===== */}
      <div className="section">
        <div className="section-head reveal">
          <span className="tag">Services AfriFuté</span>
          <h2>Votre solution mérite sa place sur l'Atlas.</h2>
          <p>On rédige votre fiche pour vous, ou vous la remplissez vous-même puis la soumettez à validation. À vous de choisir.</p>
        </div>
        <div className="idee-actions reveal" style={{ display: "flex", justifyContent: "center", gap: 14, marginTop: 8 }}>
          <button className="btn-decouvrir or" onClick={() => nav("/services")} data-testid="accueil-services-cta">Découvrir nos services</button>
        </div>
      </div>

      {/* ===== PARTENAIRE 2 - masqué jusqu'à implémentation admin ===== */}

      <div className="fil-couleur"><span className="c1"></span><span className="c2"></span><span className="c3"></span></div>

      {/* ===== CONFIANCE ===== */}
      {bande.actif && bande.items.length > 0 && (
        <div className="confiance">
          <span className="tag">Ils font confiance à AfriFuté</span>
          <div className="logos-piste-wrap">
            {/* Marquee seulement s'il y a plusieurs logos (triplé pour une boucle fluide).
                Un seul logo → affiché une fois, centré, sans défilement. */}
            <div className={`logos-piste ${bande.items.length <= 1 ? "logos-piste-statique" : ""}`}>
              {(bande.items.length > 1 ? [...bande.items, ...bande.items, ...bande.items] : bande.items).map((p, i) => {
                // Pas de logo → on affiche le nom de l'entreprise, à la même
                // taille visuelle que les logos (voir .logo-flottant-texte).
                const inner = p.logo
                  ? <img className="logo-flottant" src={logoSrc(p.logo)} alt={p.nom || ''} loading="lazy" decoding="async" />
                  : <span className="logo-flottant-texte">{p.nom}</span>;
                return (
                  <div key={i} className="logo-flottant-wrap">
                    {p.url ? <a href={p.url} target="_blank" rel="noreferrer">{inner}</a> : inner}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ===== NEWSLETTER ===== */}
      <div className="section" style={{paddingTop:'10px'}}>
        <div className="newsletter reveal">
          <h2>Fais de chaque lecture une opportunité.</h2>
          <p>Abonne-toi à notre newsletter.</p>
          <div className="newsletter-form">
            <input type="email" placeholder="Votre adresse email" />
            <button>Valider</button>
          </div>
        </div>
      </div>

    </div>
  );
}
